import { XIcon, ArrowsClockwise } from '@phosphor-icons/react';
import { AgentIcon } from '@/components/common/AgentIcon';
import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { SuggestedActionCard } from './SuggestedActionCard';
import type {
  SuggestedAction,
  SuggestedActionWithStatus,
  SuggestedActionStatus,
} from '@erp/shared-types';

interface SuggestedActionsPanelProps {
  actions: SuggestedAction[];
  onApplyAction: (action: SuggestedActionWithStatus) => void | Promise<void>;
  onDismissAction: (action: SuggestedActionWithStatus) => void;
  onReviewDraft?: (action: SuggestedActionWithStatus) => void;
  onDismissAll?: () => void;
  onRerun?: () => void;
  applyingActionIndex?: number | null;
  className?: string;
}

export function SuggestedActionsPanel({
  actions,
  onApplyAction,
  onDismissAction,
  onReviewDraft,
  onDismissAll,
  onRerun,
  applyingActionIndex = null,
  className,
}: SuggestedActionsPanelProps) {
  // Local state for optimistic UI updates within the session.
  // - dismissedIndices: Frontend-only, resets on refresh (user can temporarily hide actions)
  // - appliedIndices: Optimistic update; backend persists applied state and filters on refetch
  const [dismissedIndices, setDismissedIndices] = useState<Set<number>>(
    new Set(),
  );
  const [appliedIndices, setAppliedIndices] = useState<Set<number>>(new Set());

  // Convert actions to SuggestedActionWithStatus
  // Use action.index (the original backend index) for tracking, NOT the array position
  const actionsWithStatus: SuggestedActionWithStatus[] = useMemo(
    () =>
      actions.map((action) => {
        let status: SuggestedActionStatus = 'pending';
        if (dismissedIndices.has(action.index)) status = 'dismissed';
        if (appliedIndices.has(action.index)) status = 'applied';
        return { ...action, status };
      }),
    [actions, dismissedIndices, appliedIndices],
  );

  // Count pending actions
  const pendingActions = actionsWithStatus.filter(
    (a) => a.status === 'pending',
  );
  const pendingCount = pendingActions.length;

  // Don't render if no pending actions
  if (pendingCount === 0) {
    return null;
  }

  const handleDismiss = (action: SuggestedActionWithStatus) => {
    setDismissedIndices((prev) => new Set([...prev, action.index]));
    onDismissAction(action);
  };

  const handleApply = async (action: SuggestedActionWithStatus) => {
    await onApplyAction(action);
    setAppliedIndices((prev) => new Set([...prev, action.index]));
  };

  const handleDismissAll = () => {
    const allIndices = new Set(actions.map((action) => action.index));
    setDismissedIndices(allIndices);
    onDismissAll?.();
  };

  return (
    <div
      className={cn(
        'rounded-lg border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-900',
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-stone-200 px-3 py-2 dark:border-stone-700">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-stone-100 dark:bg-stone-800">
            <AgentIcon state="agent" size={20} />
          </div>
          <span className="text-sm font-medium text-stone-900 dark:text-stone-100">
            AI Suggested Actions ({pendingCount})
          </span>
        </div>
        <div className="flex items-center gap-1">
          {onRerun && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onRerun}
              className="h-6 px-2 text-xs text-muted-foreground hover:text-stone-900 dark:hover:text-stone-100"
            >
              <ArrowsClockwise className="mr-1 h-3 w-3" />
              Re-run
            </Button>
          )}
          {onDismissAll && pendingCount > 1 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismissAll}
              className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive"
            >
              <XIcon className="mr-1 h-3 w-3" />
              Dismiss All
            </Button>
          )}
        </div>
      </div>

      {/* Actions List */}
      <div className="flex max-h-[300px] flex-col gap-2 overflow-y-auto p-2">
        {actionsWithStatus.map((action) => (
          <SuggestedActionCard
            key={action.index}
            action={action}
            onApply={handleApply}
            onDismiss={handleDismiss}
            onReviewDraft={onReviewDraft}
            isApplying={applyingActionIndex === action.index}
          />
        ))}
      </div>
    </div>
  );
}
